import type { DNRDatabase } from '@data/db/database';
import type { Document as StoredDocument } from '@data/db/types';
import { currentDeviceId } from '@data/sync/deviceIdentity';

import { issueDocument, type IssueParams } from './documentService';
import { toPartySnapshot } from './partySnapshot';
import { listParties } from './partyReader';
import { listProducts } from './productReader';


export class QuotationServiceError extends Error {
  constructor(readonly code: 'NO_BUSINESS' | 'NOT_FOUND' | 'NOT_EDITABLE' | 'PARTY_NOT_FOUND' | 'NO_LINES' | 'NO_ITEM' | 'WRONG_BUSINESS', message: string) {
    super(message);
    this.name = 'QuotationServiceError';
  }
}

export interface QuotationLineInput {
  readonly productId: string;
  readonly quantityMilli: number;
  readonly ratePaise: number;
  readonly description?: string;
  /** Unit of measure for this line (e.g., NOS, SQFT, KG). Defaults to product's unit. */
  readonly unit?: string;
  /** Line discount type. 'NONE' = no discount. */
  readonly discountType?: 'NONE' | 'PERCENT' | 'AMOUNT';
  /** PERCENT: milli-percent (12.5% = 12500). AMOUNT: paise. Ignored when discountType is NONE. */
  readonly discountValue?: number;
  /**
   * Override GST rate in basis points (e.g., 1800 = 18%).
   * When provided, this is used instead of the product's tax rate.
   * Use 0 for 0% GST. Omit to fall back to product's tax rate.
   */
  readonly taxRateBp?: number;
}

export interface QuotationInput {
  readonly businessId: string;
  readonly customerId: string;
  readonly docDate: string;
  readonly validityDate?: string;
  readonly lines: readonly QuotationLineInput[];
  readonly roundOff?: boolean;
  readonly coverLetterEnabled: boolean;
  readonly coverLetter: string;
  readonly businessTerms?: string;
  readonly notes?: string;
  readonly placeOfSupplyOverride?: string;
  readonly now?: string;
  readonly deviceId?: string;
}

export async function quotationFormOptions(db: DNRDatabase, businessId: string) {
  return Promise.all([
    listParties(db as never, businessId, { kind: 'CUSTOMER' }),
    listProducts(db as never, businessId, { activeOnly: true }),
  ]).then(([customers, products]) => ({ customers, products }));
}

async function buildQuotation(db: DNRDatabase, input: QuotationInput, identity?: StoredDocument) {
  if (!input.lines.length) throw new QuotationServiceError('NO_LINES', 'A quotation needs at least one line.');
  const business = await db.businesses.get(input.businessId);
  if (!business) throw new QuotationServiceError('NO_BUSINESS', 'Select a business before creating a quotation.');
  const customer = await db.parties.get(input.customerId);
  if (!customer || customer.kind !== 'CUSTOMER') throw new QuotationServiceError('PARTY_NOT_FOUND', 'Choose a valid customer.');
  if (customer.businessId !== input.businessId) throw new QuotationServiceError('WRONG_BUSINESS', 'That customer belongs to another business.');
  const now = input.now ?? new Date().toISOString();
  const deviceId = input.deviceId ?? await currentDeviceId(db);
  const existingIdentity = identity ? await db.documents.get(identity.id) : undefined;
  if (identity && (!existingIdentity || existingIdentity.status !== 'DRAFT' || existingIdentity.docType !== 'QUOTATION')) {
    throw new QuotationServiceError('NOT_EDITABLE', 'Only quotation drafts can be edited.');
  }
  const products = await Promise.all(input.lines.map(async (line) => {
    const product = await db.products.get(line.productId);
    if (!product || product.businessId !== input.businessId) throw new QuotationServiceError('NO_ITEM', 'A selected product is unavailable.');
    const tax = product.taxRateId ? await db.taxRates.get(product.taxRateId) : undefined;
    const productTaxRateBp = tax?.rateBp ?? 0;
    return { line, product, taxRateBp: line.taxRateBp ?? productTaxRateBp };
  }));
  const pos = input.placeOfSupplyOverride || customer.shippingStateCode || customer.stateCode || '';
  const params: IssueParams = {
    businessId: input.businessId,
    businessStateCode: business.stateCode,
    docType: 'QUOTATION',
    docDate: input.docDate,
    customerId: customer.id,
    partySnapshot: toPartySnapshot(customer),
    placeOfSupplyCode: pos,
    deliveryAddressLines: customer.shippingAddressLines ? [...customer.shippingAddressLines] : undefined,
    deliveryStateCode: customer.shippingStateCode,
    deliveryStateName: customer.shippingStateName,
    lines: products.map(({ line, product, taxRateBp }) => ({
      productId: product.id,
      description: line.description?.trim() || product.name,
      hsnSac: product.hsnSac,
      unit: line.unit ?? product.unit,
      quantityMilli: line.quantityMilli,
      ratePaise: line.ratePaise,
      discountType: line.discountType ?? 'NONE',
      discountValue: line.discountValue ?? 0,
      taxRateBp: taxRateBp,
      cessRateBp: product.cessRateBp,
      isStockTracked: product.isStockTracked,
    })),
    notes: input.notes,
    terms: input.businessTerms,
    coverLetter: input.coverLetter,
    coverLetterEnabled: input.coverLetterEnabled,
    expiryDate: input.validityDate,
    roundOff: input.roundOff ?? false,
    status: 'DRAFT',
    ...(existingIdentity ? { replaceIdentity: { id: existingIdentity.id, docNumber: existingIdentity.docNumber } } : {}),
    user: { id: 'local', name: 'You' },
    now,
    deviceId,
    fyStartMonth: business.fyStartMonth,

  };
  const result = await db.transaction(
    'rw',
    [db.documents, db.documentLines, db.documentLinks, db.stockTransactions,
      db.documentSequences, db.auditLogs, db.parties, db.businesses, db.syncOutbox],
    () => issueDocument(db as never, params),
  );

  return { id: result.id, docNumber: result.docNumber, grandTotalPaise: result.grandTotalPaise };
}

export async function createQuotationDraft(db: DNRDatabase, input: QuotationInput) {
  return buildQuotation(db, input);
}

export async function updateQuotationDraft(db: DNRDatabase, id: string, input: Omit<QuotationInput, 'businessId'>) {
  const existing = await db.documents.get(id);
  if (!existing || existing.docType !== 'QUOTATION') throw new QuotationServiceError('NOT_FOUND', 'Quotation not found.');
  if (existing.status !== 'DRAFT') throw new QuotationServiceError('NOT_EDITABLE', 'Issued quotations are immutable.');
  return buildQuotation(db, { ...input, businessId: existing.businessId, placeOfSupplyOverride: input.placeOfSupplyOverride ?? existing.placeOfSupplyCode }, existing);
}
